/**
 * Sync Error Adapters
 *
 * Adapters for converting between SyncInitReason and SyncErrorCode.
 * These functions bridge the gap between the sync initialization layer
 * (which uses SyncInitReason) and the error messaging layer (which uses SyncErrorCode).
 *
 * **Adapter Boundary**: Keeps Phase-2 message utilities stable while allowing
 * Phase-3+ connection logic to use SyncInitReason. This prevents cascading changes
 * when new reasons are added.
 *
 * @module syncErrorAdapters
 */

import type { SyncInitReason } from '@/types/sync';
import type { SyncErrorCode } from '@/types/sync/SyncProvider';

/**
 * Maps SyncInitReason to SyncErrorCode for error message lookup.
 *
 * This adapter is needed because:
 * - SyncInitReason is used in connection logic (useProviderConnection, SyncInitTypes)
 * - SyncErrorCode is used for error message utilities (getSyncErrorMessage)
 * - We need to bridge these two type systems for proper error display
 *
 * Mapping Strategy:
 * - Direct mappings where names match (TOKEN_EXPIRED, PERMISSION_DENIED)
 * - Semantic mappings where concepts align (OFFLINE → NETWORK_ERROR)
 * - Fallback to UNKNOWN_ERROR for unmapped cases
 *
 * @param reason - The SyncInitReason to convert
 * @returns The corresponding SyncErrorCode
 *
 * @example
 * ```typescript
 * const reason: SyncInitReason = 'TOKEN_EXPIRED';
 * const code = syncInitReasonToErrorCode(reason);
 * // Returns: 'NOT_CONNECTED' (token issues prevent connection)
 * ```
 */
export function syncInitReasonToErrorCode(reason: SyncInitReason): SyncErrorCode {
  switch (reason) {
    // Success case - map to UNKNOWN_ERROR (shouldn't be used for errors)
    case 'SUCCESS':
      return 'UNKNOWN_ERROR';

    // Token/Auth issues - all prevent connection
    case 'TOKEN_EXPIRED':
    case 'TOKEN_REVOKED':
      return 'NOT_CONNECTED';

    // Permission issues
    case 'PERMISSION_DENIED':
      return 'PERMISSION_DENIED';

    // Directory/File issues
    case 'DIRECTORY_INVALID':
      return 'FILE_NOT_FOUND';

    // Network issues
    case 'OFFLINE':
    case 'TIMEOUT':
    case 'PROVIDER_OUTAGE':
    case 'RATE_LIMITED':
      return 'NETWORK_ERROR';

    // Cancellation - map to NOT_CONNECTED (user chose not to connect)
    case 'USER_CANCELLED':
    case 'ACCESS_DENIED':
    case 'ABORTED':
      return 'NOT_CONNECTED';

    // OAuth scope denial - map to PERMISSION_DENIED (denied specific permission)
    case 'INSUFFICIENT_SCOPES':
      return 'PERMISSION_DENIED';

    // Unknown errors
    case 'UNKNOWN_ERROR':
      return 'UNKNOWN_ERROR';

    // Exhaustiveness check
    default: {
      const _exhaustive: never = reason;
      console.warn('[syncErrorAdapters] Unknown SyncInitReason:', _exhaustive);
      return 'UNKNOWN_ERROR';
    }
  }
}
